// PATH: src/lib/curriculumTree.ts — arbre de sélection du programme marocain
// (onboarding) : fonctions PURES, aucun appel réseau.
//
// Une FEUILLE est un niveau importable : un niveau sans filière, ou un couple
// niveau + filière. Sa clé « <grade>|<filière> » est exactement ce que le
// backend reçoit (CurriculumSelection) et ce qu'il range dans embed_grade_id /
// embed_track_id — d'où la comparaison avec les niveaux déjà importés.
import type { CurriculumSelection } from "../api/curriculum";
import type { EmbedGrade, EmbedStage, EmbedTrack } from "../api/embedCatalog";
import type { Level } from "../models/school";

export interface CatalogGradeNode {
  grade: EmbedGrade;
  /** Vide = niveau sans filière. */
  tracks: EmbedTrack[];
}

export interface CatalogStageNode {
  stage: EmbedStage;
  grades: CatalogGradeNode[];
}

/** Forme attendue par le Tree d'antd (sous-ensemble de DataNode). */
export interface CurriculumTreeNode {
  key: string;
  title: string;
  disableCheckbox?: boolean;
  children?: CurriculumTreeNode[];
}

/** Clé d'une feuille. Les UUID sont comparés en minuscules (Postgres les rend ainsi). */
export const selectionKey = (gradeId: string, trackId?: string | null): string =>
  `${gradeId.trim().toLowerCase()}|${(trackId ?? "").trim().toLowerCase()}`;

export const isLeafKey = (key: string): boolean => key.includes("|");

export const parseSelectionKey = (key: string): CurriculumSelection => {
  const [grade_id, track_id] = key.split("|");
  return track_id ? { grade_id, track_id } : { grade_id };
};

/** Feuilles d'un niveau : lui-même sans filière, sinon une par filière. */
const leavesOf = ({ grade, tracks }: CatalogGradeNode): string[] =>
  tracks.length === 0 ? [selectionKey(grade.id)] : tracks.map((t) => selectionKey(grade.id, t.id));

export const allLeafKeys = (stages: CatalogStageNode[]): string[] =>
  stages.flatMap((s) => s.grades.flatMap(leavesOf));

/**
 * Données du Tree. Un cycle sans niveau est masqué ; une feuille déjà
 * importée est cochée par l'appelant et non décochable ici.
 */
export const buildTreeData = (stages: CatalogStageNode[], imported: Set<string>): CurriculumTreeNode[] =>
  stages
    .filter((s) => s.grades.length > 0)
    .map(({ stage, grades }) => ({
      key: `stage:${stage.id}`,
      title: stage.name,
      children: grades.map(({ grade, tracks }) => {
        if (tracks.length === 0) {
          const key = selectionKey(grade.id);
          return { key, title: grade.name, disableCheckbox: imported.has(key) };
        }
        return {
          key: `grade:${grade.id}`,
          title: grade.name,
          children: tracks.map((t) => {
            const key = selectionKey(grade.id, t.id);
            return { key, title: t.name, disableCheckbox: imported.has(key) };
          }),
        };
      }),
    }));

/** Niveaux du catalogue déjà importés par l'école. */
export const importedKeys = (levels: Pick<Level, "embed_grade_id" | "embed_track_id">[]): Set<string> =>
  new Set(
    levels
      .filter((l) => !!l.embed_grade_id)
      .map((l) => selectionKey(l.embed_grade_id as string, l.embed_track_id))
  );

/** Sélections à envoyer : feuilles cochées, hors niveaux déjà importés. */
export const selectionsFromChecked = (checked: readonly (string | number)[], imported: Set<string>): CurriculumSelection[] =>
  checked
    .map(String)
    .filter((k) => isLeafKey(k) && !imported.has(k))
    .map(parseSelectionKey);

/** Libellé d'une feuille pour le récapitulatif : « 2e année bac — Sciences physiques ». */
export const leafLabel = (stages: CatalogStageNode[], key: string): string => {
  for (const { grades } of stages) {
    for (const { grade, tracks } of grades) {
      if (tracks.length === 0 && selectionKey(grade.id) === key) return grade.name;
      const track = tracks.find((t) => selectionKey(grade.id, t.id) === key);
      if (track) return `${grade.name} — ${track.name}`;
    }
  }
  return key;
};
